const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
// Single activation-required policy shared by the login and recovery forms.
// DISABLED, INVITED, and DECLINED accounts cannot sign in with a password
// and must complete account activation first.
const ACTIVATION_STATES = new Set(["DISABLED", "INVITED", "DECLINED"]);

customElements.define(
  "custom-main-login",
  class extends HTMLElement {
    constructor() {
      super();

      this.bound = false;
    }

    connectedCallback() {
      if (this.bound) return;
      this.bound = true;

      this.#bindEmailSubscription();

      this.addEventListener("login:activation-required", (event) => {
        document
          .getElementById("ActivateAccountPopup")
          ?.show(event.detail?.opener);
      });
      this.addEventListener("recovery:activation-required", (event) => {
        document
          .getElementById("RecoverActivateAccountPopup")
          ?.show(event.detail?.opener);
      });

      // Post-submit navigation is server-driven: a successful Shopify
      // recovery submission re-renders this section with
      // data-view="confirmation". Nothing to settle client-side.
      this.addEventListener("recovery:submitted", () => {});

      this.#handleViewState({ focus: false, reset: false });
      window.addEventListener("hashchange", () =>
        this.#handleViewState({ focus: true, reset: true }),
      );
    }

    // Shared email format check. Children call this instead of keeping
    // their own copy of the regex.
    isValidEmail(value) {
      return EMAIL_REGEX.test(value ?? "");
    }

    // Single definition of "needs activation", shared by both forms.
    requiresActivation(customer) {
      const customerState =
        typeof customer?.state === "string" ? customer.state.toUpperCase() : "";
      return ACTIVATION_STATES.has(customerState);
    }

    // The single customer lookup service.
    // Resolves to { status: 'found' | 'not_found' | 'error', customer } so
    // children can distinguish an unknown member from a failed request.
    async lookupCustomer(email) {
      try {
        const responseJson = await this.#requestCustomerInfo(email);
        const customer = responseJson?.data;
        if (!customer) return { status: "not_found", customer: null };
        return { status: "found", customer };
      } catch (error) {
        console.error(error.message);
        return { status: "error", customer: null };
      }
    }

    #bindEmailSubscription() {
      const checkbox = this.querySelector("#ContactForm-checkbox4");
      if (!checkbox) return;

      this.#handleCheckInitEmail();
      checkbox.addEventListener("change", (event) => {
        localStorage.setItem(
          "emailYN",
          event.currentTarget.checked ? "Y" : "N",
        );
      });
    }

    #handleCheckInitEmail() {
      const localEmailYN = localStorage.getItem("emailYN");
      if (!localEmailYN) {
        localStorage.setItem("emailYN", "N");
      }
    }

    async #requestCustomerInfo(email = "") {
      const response = await fetch(window.middlewareUrls.customerInfo, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error(`Response status: ${response.status}`);
      }

      return response.json();
    }

    #handleViewState({ focus, reset } = {}) {
      if (this.getAttribute("data-view") === "confirmation") {
        // A retained #recover fragment must not hide the confirmation view.
        if (window.location.hash === "#recover") {
          history.replaceState(
            null,
            "",
            window.location.pathname + window.location.search,
          );
        }
        return;
      }

      if (window.location.hash === "#recover") {
        this.querySelector("custom-recover-customer-password")?.focusHeading();
        return;
      }

      // Login view (no hash or #login, e.g. Cancel): on an actual toggle,
      // reset the recovery form so a return visit starts clean, and move
      // focus to the login heading. Skipped on initial page load so
      // server-rendered state is never wiped or refocused unexpectedly.
      if (reset) {
        this.querySelector("custom-recover-customer-password")?.resetForm();
      }
      if (focus) {
        this.querySelector("custom-customer-login")?.focusHeading();
      }
    }
  },
);
